<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import { InputEmits, InputProps } from '@/deep-form/models/dform-input-core';
import { CustomColumnDefinition } from '@/components/ui/DownlineTable.ts';
import * as src from '../lib/sources';
import { InputDictionaryItem } from '@/deep-form/components/InputDictionary';

// Define the type for our custom columns editor
type CustomColumnsEditorValue = Record<string, CustomColumnDefinition>;

const props = defineProps<InputProps<CustomColumnsEditorValue, null>>();
const emit = defineEmits<InputEmits<CustomColumnsEditorValue>>();

// Default model value
const defaultValue: CustomColumnsEditorValue = {};

// Local model to track the current value
const localValue = ref<CustomColumnsEditorValue>(props.modelValue || defaultValue);

// Available properties for dropdowns
const availableProperties = ref<InputDictionaryItem[]>([]);

// Fetch properties
onMounted(async () => {
  availableProperties.value = await src.properties();
});

// Convert localValue to array for easier rendering
const columnsArray = computed(() => {
  return Object.entries(localValue.value || {}).map(([key, value]) => ({
    key,
    ...value
  }));
});

// Add new custom column
const addColumn = () => {
  if (!localValue.value) {
    localValue.value = {};
  }

  // Use first available property as default key
  const defaultProperty = availableProperties.value.length > 0
    ? (typeof availableProperties.value[0] === 'string'
        ? availableProperties.value[0]
        : String(availableProperties.value[0].value || ''))
    : 'property';

  localValue.value[defaultProperty] = {
    primaryField: defaultProperty,
    secondaryField: '',
    template: '{primary_field} - {secondary_field}'
  };

  updateModel();
};

// Remove a custom column
const removeColumn = (key: string) => {
  if (localValue.value && localValue.value[key]) {
    delete localValue.value[key];
    updateModel();
  }
};

// Update column property
const updateColumnProperty = (key: string, field: keyof CustomColumnDefinition, value: string) => {
  if (!localValue.value || !localValue.value[key]) return;

  // If updating primaryField, we need to move the entire entry to a new key
  if (field === 'primaryField' && value !== key) {
    const oldData = { ...localValue.value[key] };
    oldData[field] = value;

    // Remove old entry
    delete localValue.value[key];

    // Add new entry with primaryField as key
    localValue.value[value] = oldData;
  } else {
    localValue.value[key][field] = value;
  }

  updateModel();
};

// Update model value
const updateModel = () => {
  emit('update:modelValue', { ...localValue.value });
};

// Clean the model when component is mounted to ensure proper structure
const ensureProperModel = () => {
  if (!localValue.value) {
    localValue.value = {};
  }
  updateModel();
};

// Call this on mounted
ensureProperModel();

// Get property title for display
const getPropertyTitle = (value: string) => {
  const prop = availableProperties.value.find(p =>
    (typeof p === 'string' ? p : p.value) === value
  );
  return typeof prop === 'string' ? prop : (prop?.title || value);
};

// Template examples for help
const templateExamples = [
  '{primary_field} - {secondary_field}',
  '{primary_field} ({secondary_field})',
  'Level {primary_field}, Position: {secondary_field}',
  '<strong>{primary_field}</strong> <em>({secondary_field})</em>'
];
</script>

<template>
  <div class="custom-columns-editor">
    <v-card variant="outlined">
             <v-card-title class="d-flex align-center">
         <v-icon class="mr-2">mdi-table-edit</v-icon>
         Column Display Override
         <v-spacer />
         <v-btn
           color="primary"
           variant="text"
           size="small"
           @click="addColumn"
         >
           <v-icon>mdi-plus</v-icon>
           Override Column
         </v-btn>
       </v-card-title>

      <v-card-text>
        <div v-if="columnsArray.length === 0" class="text-center py-8 text-grey">
          <v-icon size="48" class="mb-2">mdi-table-column-plus-before</v-icon>
          <div>No column overrides defined</div>
          <div class="text-caption">Click "Add Column" to override the display of an existing column</div>
        </div>

        <div v-else>
          <v-row
            v-for="(column, index) in columnsArray"
            :key="column.key"
            class="mb-4"
          >
            <v-col cols="12">
              <v-card variant="outlined" class="custom-column-card">
                <v-card-text class="pb-2">
                                     <v-row>
                     <!-- Primary Field (which is also the key) -->
                     <v-col cols="12" md="4">
                       <v-autocomplete
                         :model-value="column.primaryField"
                         @update:model-value="(value) => updateColumnProperty(column.key, 'primaryField', value)"
                         :items="availableProperties"
                         item-title="title"
                         item-value="value"
                         label="Primary Field (Column to override)"
                         density="compact"
                         variant="outlined"
                         hide-details
                         clearable
                         class="mb-2"
                       />
                     </v-col>

                     <!-- Secondary Field -->
                     <v-col cols="12" md="4">
                       <v-autocomplete
                         :model-value="column.secondaryField"
                         @update:model-value="(value) => updateColumnProperty(column.key, 'secondaryField', value)"
                         :items="availableProperties"
                         item-title="title"
                         item-value="value"
                         label="Secondary Field"
                         density="compact"
                         variant="outlined"
                         hide-details
                         clearable
                         class="mb-2"
                       />
                     </v-col>

                     <!-- Actions -->
                     <v-col cols="12" md="4" class="d-flex align-center">
                       <v-btn
                         color="error"
                         variant="text"
                         size="small"
                         @click="removeColumn(column.key)"
                         class="ml-auto"
                       >
                         <v-icon>mdi-delete</v-icon>
                         Remove
                       </v-btn>
                     </v-col>
                   </v-row>

                  <!-- Template -->
                  <v-row>
                    <v-col cols="12">
                      <v-text-field
                        :model-value="column.template"
                        @update:model-value="(value) => updateColumnProperty(column.key, 'template', value)"
                        label="Template"
                        placeholder="{primary_field} - {secondary_field}"
                        density="compact"
                        variant="outlined"
                        hide-details
                      >
                        <template #append-inner>
                          <v-menu>
                            <template #activator="{ props: menuProps }">
                              <v-btn
                                v-bind="menuProps"
                                variant="text"
                                size="small"
                                icon="mdi-help-circle"
                              />
                            </template>
                            <v-card max-width="400">
                              <v-card-title>Template Examples</v-card-title>
                              <v-card-text>
                                <div class="text-caption mb-2">
                                  Use <code>{primary_field}</code> and <code>{secondary_field}</code> placeholders:
                                </div>
                                <v-list density="compact">
                                  <v-list-item
                                    v-for="example in templateExamples"
                                    :key="example"
                                    @click="updateColumnProperty(column.key, 'template', example)"
                                    class="text-caption cursor-pointer"
                                  >
                                    <code>{{ example }}</code>
                                  </v-list-item>
                                </v-list>
                              </v-card-text>
                            </v-card>
                          </v-menu>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <!-- Preview -->
                  <v-row v-if="column.primaryField && column.secondaryField">
                    <v-col cols="12">
                      <v-alert
                        type="info"
                        variant="outlined"
                        density="compact"
                        class="mb-0"
                      >
                        <template #title>
                          <span class="text-caption">Preview:</span>
                        </template>
                        <div class="text-caption">
                          {{ column.template
                            .replace('{primary_field}', `[${getPropertyTitle(column.primaryField)}]`)
                            .replace('{secondary_field}', `[${getPropertyTitle(column.secondaryField)}]`)
                          }}
                        </div>
                      </v-alert>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <!-- Help Text -->
        <v-alert
          type="info"
          variant="outlined"
          class="mt-4"
        >
          <template #title>How to use Custom Columns</template>
          <div class="text-body-2">
            <ol class="pl-4">
              <li>Override the display of existing columns by combining values from two fields</li>
              <li>Select <strong>Primary Field</strong> - this is the column you want to override</li>
              <li>Select <strong>Secondary Field</strong> - additional field to combine with the primary</li>
              <li>Create a <strong>Template</strong> using <code>{primary_field}</code> and <code>{secondary_field}</code> placeholders</li>
              <li><strong>Important:</strong> The Primary Field must already be in your Properties list</li>
            </ol>
            <div class="mt-2 pa-2 bg-grey-lighten-4 rounded">
              <strong>Example:</strong> If <code>vmdl.level</code> is in Properties and shows "3",
              you can override it to show "Level 3 (Position: 5)" by combining with <code>position.tree_level</code>
            </div>
          </div>
        </v-alert>
      </v-card-text>
    </v-card>
  </div>
</template>

<style scoped>
.custom-columns-editor {
  width: 100%;
}

.custom-column-card {
  background-color: rgba(0, 0, 0, 0.02);
}

.cursor-pointer {
  cursor: pointer;
}

.cursor-pointer:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

code {
  background-color: rgba(0, 0, 0, 0.1);
  padding: 2px 4px;
  border-radius: 3px;
  font-size: 0.85em;
}
</style>
